import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import {
  defaultBehavior,
  getConnectorFieldSchema,
  proposeFieldMappings,
  type FieldMapping,
  type SyncBehaviorConfig,
} from "@attendee-sync/connectors";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { StatusBadge } from "@/components/status/status-badge";
import { api } from "@/lib/api";
import { AUTO_STREAM_FREE_ATTENDEE_LIMIT } from "@/lib/trial";

type DestinationOption = {
  provider: string;
  label: string;
  configured: boolean;
  credentialId: string | null;
};

type SourceOption = {
  id: string;
  provider: string;
  email: string;
  displayName: string;
  status: string;
};

type AutoStreamTemplate = {
  accountId: string;
  enabled: boolean;
  destinationCredentialId?: string | null;
  destinationProvider?: string | null;
  sourceConnectionIds: string[];
  mappingJson?: string | null;
  scheduleIntervalSeconds?: number | null;
  updatedAt: number;
};

type AutoStreamsResponse = {
  template: AutoStreamTemplate;
  billingStatus: string;
  canEnable: boolean;
};

const SCHEDULE_OPTIONS = [
  { label: "Every 15 Minutes", seconds: 900 },
  { label: "Every 30 Minutes", seconds: 1800 },
  { label: "Every Hour", seconds: 3600 },
  { label: "Every 6 Hours", seconds: 21600 },
  { label: "Every Day", seconds: 86400 },
];

function parseTemplateMapping(raw?: string | null): {
  fieldMappings: FieldMapping[];
  behavior: SyncBehaviorConfig;
} {
  try {
    const parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    const behavior = {
      ...defaultBehavior(),
      ...(typeof parsed.behavior === "object" && parsed.behavior
        ? (parsed.behavior as SyncBehaviorConfig)
        : {}),
    };
    const fieldMappings = Array.isArray(parsed.fieldMappings)
      ? (parsed.fieldMappings as FieldMapping[])
      : [];
    return { fieldMappings, behavior };
  } catch {
    return { fieldMappings: [], behavior: defaultBehavior() };
  }
}

function buildDefaultMappings(destinationProvider: string): FieldMapping[] {
  const source = getConnectorFieldSchema("zoom");
  const dest = getConnectorFieldSchema(destinationProvider);
  if (!source || !dest) return [];
  return proposeFieldMappings(source.fields, dest.fields);
}

export function AutoCreateStreamsPanel({
  orgId,
  canManage,
  destinations,
  sourceConnections,
}: {
  orgId: string;
  canManage: boolean;
  destinations: DestinationOption[];
  sourceConnections: SourceOption[];
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [canEnable, setCanEnable] = useState(false);
  const [billingStatus, setBillingStatus] = useState("none");

  const [enabled, setEnabled] = useState(false);
  const [destinationCredentialId, setDestinationCredentialId] = useState("");
  const [destinationProvider, setDestinationProvider] = useState("");
  const [sourceConnectionIds, setSourceConnectionIds] = useState<string[]>([]);
  const [scheduleIntervalSeconds, setScheduleIntervalSeconds] = useState(3600);
  const [existingRecordAction, setExistingRecordAction] =
    useState<SyncBehaviorConfig["existingRecordAction"]>("update");
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
  const [baseline, setBaseline] = useState("");

  const connectedDestinations = destinations.filter(
    (d) => d.configured && d.credentialId,
  );
  const activeSources = sourceConnections.filter((s) => s.status === "active");

  const currentSnapshot = useMemo(
    () =>
      JSON.stringify({
        enabled,
        destinationCredentialId,
        destinationProvider,
        sourceConnectionIds: [...sourceConnectionIds].sort(),
        scheduleIntervalSeconds,
        existingRecordAction,
        fieldMappings,
      }),
    [
      enabled,
      destinationCredentialId,
      destinationProvider,
      sourceConnectionIds,
      scheduleIntervalSeconds,
      existingRecordAction,
      fieldMappings,
    ],
  );
  const dirty = baseline !== "" && currentSnapshot !== baseline;

  async function load() {
    const res = await api<AutoStreamsResponse>(
      `/api/accounts/${orgId}/auto-streams`,
    );
    const t = res.template;
    const parsed = parseTemplateMapping(t.mappingJson);
    const destProvider = t.destinationProvider ?? "";
    const mappings =
      parsed.fieldMappings.length > 0
        ? parsed.fieldMappings
        : destProvider
          ? buildDefaultMappings(destProvider)
          : [];
    const next = {
      enabled: t.enabled,
      destinationCredentialId: t.destinationCredentialId ?? "",
      destinationProvider: destProvider,
      sourceConnectionIds: t.sourceConnectionIds ?? [],
      scheduleIntervalSeconds: t.scheduleIntervalSeconds ?? 3600,
      existingRecordAction: parsed.behavior.existingRecordAction ?? "update",
      fieldMappings: mappings,
    };
    setEnabled(next.enabled);
    setDestinationCredentialId(next.destinationCredentialId);
    setDestinationProvider(next.destinationProvider);
    setSourceConnectionIds(next.sourceConnectionIds);
    setScheduleIntervalSeconds(next.scheduleIntervalSeconds);
    setExistingRecordAction(next.existingRecordAction);
    setFieldMappings(next.fieldMappings);
    setCanEnable(res.canEnable);
    setBillingStatus(res.billingStatus);
    setBaseline(
      JSON.stringify({
        enabled: next.enabled,
        destinationCredentialId: next.destinationCredentialId,
        destinationProvider: next.destinationProvider,
        sourceConnectionIds: [...next.sourceConnectionIds].sort(),
        scheduleIntervalSeconds: next.scheduleIntervalSeconds,
        existingRecordAction: next.existingRecordAction,
        fieldMappings: next.fieldMappings,
      }),
    );
  }

  useEffect(() => {
    setLoading(true);
    void load()
      .catch((e) =>
        setError(
          e instanceof Error ? e.message : "Could not load Auto-Create Streams",
        ),
      )
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  function onDestinationChange(credentialId: string) {
    const dest = connectedDestinations.find(
      (d) => d.credentialId === credentialId,
    );
    setDestinationCredentialId(credentialId);
    setDestinationProvider(dest?.provider ?? "");
    if (dest?.provider) {
      setFieldMappings(buildDefaultMappings(dest.provider));
    }
  }

  function toggleSource(id: string) {
    setSourceConnectionIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  async function save() {
    if (!canManage || saving || !dirty) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const mappingJson = JSON.stringify({
        fieldMappings,
        behavior: { ...defaultBehavior(), existingRecordAction },
      });
      await api(`/api/accounts/${orgId}/auto-streams`, {
        method: "PUT",
        body: JSON.stringify({
          enabled,
          destinationCredentialId: destinationCredentialId || null,
          destinationProvider: destinationProvider || null,
          sourceConnectionIds,
          mappingJson,
          scheduleIntervalSeconds,
        }),
      });
      setMessage("Auto-Create Streams settings saved.");
      await load();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Could not save Auto-Create Streams",
      );
    } finally {
      setSaving(false);
    }
  }

  function cancelEdits() {
    void load().catch(() => undefined);
    setError(null);
    setMessage(null);
  }

  return (
    <Panel>
      <PanelHeader title="Auto-Create Streams" />
      <div className="space-y-4 p-5">
        <p className="text-sm text-rs-ink-muted">
          When enabled during trial or on a paid workspace, AttendeeSync
          discovers new events from connected sources (Zoom, Google Meet,
          LinkedIn Events, HeySummit, Fluent Forms) and creates sync streams
          from this template. Auto-created streams with{" "}
          {AUTO_STREAM_FREE_ATTENDEE_LIMIT} or fewer attendees are free (no
          stream fee and no attendee fee); larger meetings bill normally.
        </p>

        {loading ? (
          <p className="text-sm text-rs-ink-muted">Loading…</p>
        ) : (
          <>
            {!canEnable ? (
              <div className="rounded-rs border border-rs-border bg-rs-bg p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge tone="needs_configuration">
                    Trial Or Paid Required
                  </StatusBadge>
                  <span className="text-sm text-rs-ink-muted">
                    Billing status: {billingStatus}
                  </span>
                </div>
                <p className="mt-2 text-sm text-rs-ink-muted">
                  Start or resume a free trial, or upgrade to paid billing, to
                  enable Auto-Create Streams.
                </p>
                {canManage ? (
                  <Button asChild size="sm" className="mt-3">
                    <Link to="/settings/billing">Open Billing</Link>
                  </Button>
                ) : null}
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-rs-ink">
                <input
                  type="checkbox"
                  className="size-4 rounded border-rs-border"
                  checked={enabled}
                  disabled={!canManage || !canEnable}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
                Enable Auto-Create Streams
              </label>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="auto-dest">Default Destination</Label>
                <select
                  id="auto-dest"
                  className="h-12 w-full rounded-rs border border-rs-border bg-rs-surface px-4 text-sm text-rs-ink"
                  value={destinationCredentialId}
                  disabled={!canManage || !canEnable}
                  onChange={(e) => onDestinationChange(e.target.value)}
                >
                  <option value="">Select a destination…</option>
                  {connectedDestinations.map((d) => (
                    <option key={d.credentialId!} value={d.credentialId!}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="auto-schedule">Sync Schedule</Label>
                <select
                  id="auto-schedule"
                  className="h-12 w-full rounded-rs border border-rs-border bg-rs-surface px-4 text-sm text-rs-ink"
                  value={scheduleIntervalSeconds}
                  disabled={!canManage || !canEnable}
                  onChange={(e) =>
                    setScheduleIntervalSeconds(Number(e.target.value))
                  }
                >
                  {SCHEDULE_OPTIONS.map((o) => (
                    <option key={o.seconds} value={o.seconds}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="auto-behavior">
                  When a Contact Already Exists
                </Label>
                <select
                  id="auto-behavior"
                  className="h-12 w-full rounded-rs border border-rs-border bg-rs-surface px-4 text-sm text-rs-ink"
                  value={existingRecordAction}
                  disabled={!canManage || !canEnable}
                  onChange={(e) =>
                    setExistingRecordAction(
                      e.target
                        .value as SyncBehaviorConfig["existingRecordAction"],
                    )
                  }
                >
                  <option value="update">Update Existing</option>
                  <option value="skip">Skip Existing</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-rs-ink">
                Source Connections
              </p>
              <p className="text-sm text-rs-ink-muted">
                Leave all unchecked to use every connected source account.
              </p>
              {activeSources.length === 0 ? (
                <p className="text-sm text-rs-ink-muted">
                  Connect a source above before enabling.
                </p>
              ) : (
                <ul className="space-y-2">
                  {activeSources.map((s) => (
                    <li key={s.id}>
                      <label className="flex items-center gap-2 text-sm text-rs-ink">
                        <input
                          type="checkbox"
                          className="size-4 rounded border-rs-border"
                          checked={sourceConnectionIds.includes(s.id)}
                          disabled={!canManage || !canEnable}
                          onChange={() => toggleSource(s.id)}
                        />
                        {s.provider === "google_meet"
                          ? "Google Meet"
                          : s.provider === "linkedin_events"
                            ? "LinkedIn Events"
                            : s.provider === "heysummit"
                              ? "HeySummit"
                              : s.provider === "fluent_forms"
                                ? "Fluent Forms"
                                : s.provider === "constant_contact"
                                  ? "Constant Contact"
                                  : "Zoom"}{" "}
                        — {s.displayName || s.email}
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="space-y-1">
              <p className="text-sm font-medium text-rs-ink">Field Mappings</p>
              <p className="text-sm text-rs-ink-muted">
                {fieldMappings.length > 0
                  ? `${fieldMappings.length} default field mappings will be applied to auto-created streams.`
                  : "Select a destination to generate default field mappings."}
              </p>
            </div>

            {error ? (
              <p role="alert" className="text-sm text-rs-danger">
                {error}
              </p>
            ) : null}
            {message ? (
              <p className="text-sm text-rs-success">{message}</p>
            ) : null}

            {canManage && canEnable ? (
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!dirty || saving}
                  onClick={cancelEdits}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  disabled={!dirty || saving}
                  onClick={() => void save()}
                >
                  {saving ? "Saving…" : "Save Changes"}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </Panel>
  );
}
